import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, User } from '../services/authService';
import TopBar from '../components/TopBar';
import Button from '../components/Button';
import defaultProfilePic from '../assets/default-profile.jpg';

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  photoUrl?: string;
  role: string;
  businessId: string;
}

interface Business {
  id: string;
  name: string;
}

const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

const PageTitle = styled.h1`
  font-size: 38px;
  font-weight: 800;
  color: #333;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
  width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const BreadcrumbText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
  
  @media (max-width: 480px) {
    max-width: 80px;
  }
`;

const StaffList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const StaffCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StaffInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const ProfileImage = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StaffDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const StaffName = styled.h3`
  font-size: 24px;
  font-weight: 900;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StaffEmail = styled.p`
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StaffRole = styled.p`
  margin: 0;
  font-weight: 600;
`;

const StaffRoleAndEmail = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  min-width: 0; /* Prevent overflow */
`;

// Updated to hide on mobile screens
const ActionButton = styled.div`
  flex-shrink: 0;
  margin-left: 16px;
  
  @media (max-width: 768px) {
    display: none; /* Hide edit button on mobile */
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  background-color: white;
  border-radius: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
`;

// Custom styled outlined button wrapper
const OutlineButtonWrapper = styled.div`
  // button {
  //   background-color: transparent !important;
  //   color: #000 !important;
  //   border: 1px solid #000 !important;
  //   box-shadow: none !important;
    
  //   &:hover {
  //     background-color: rgba(0, 0, 0, 0.05) !important;
  //   }
  // }
`;

const BusinessStaffPage: React.FC = () => {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!businessId) return;

      try {
        setLoading(true);
        const userData = await getCurrentUser();
        setUser(userData);

        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        
        if (businessDoc.exists()) {
          setBusiness({
            id: businessDoc.id,
            name: businessDoc.data().name
          });
          
          // Fetch staff members for this business
          const staffQuery = query(
            collection(db, 'staff'),
            where('businessId', '==', businessId)
          );
          
          const staffSnapshot = await getDocs(staffQuery);
          const staffData: StaffMember[] = [];
          
          staffSnapshot.forEach(doc => {
            staffData.push({
              id: doc.id,
              ...(doc.data() as Omit<StaffMember, 'id'>)
            });
          });
          
          setStaffMembers(staffData);
        } else {
          console.error("Business not found");
          navigate('/businesses');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId, navigate]);

  const handleNavigateToMyAccount = () => {
    navigate('/dashboard');
  };

  const handleNavigateToMyBusinesses = () => {
    navigate('/businesses');
  };
  
  const handleNavigateToSettings = () => {
    navigate(`/business/${businessId}/settings`);
  };
  
  const handleAddStaff = () => {
    navigate(`/business/${businessId}/staff/add`);
  };
  
  const handleEditStaff = (staffId: string) => {
    navigate(`/business/${businessId}/staff/${staffId}/edit`);
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <TopBar user={user} />
          <div>Loading...</div>
        </ContentWrapper>
      </Container>
    );  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Business Staff</title>
        <meta name="description" content={`${APP_NAME} - Business Staff`} />
      </Helmet>
      <Container>
      <ContentWrapper>
        <TopBar user={user} />
        
        <HeaderSection>
          <PageTitle>Team</PageTitle>
          {/* Use the updated responsive Button component */}
          <Button 
            text="Add" 
            onClick={handleAddStaff}
            bgColor="#000"
            textColor="#fff"
            width="auto"
            minWidth="100px"
            icon="+"
            responsive={true} // Enable responsive mode
          />
        </HeaderSection>
        
        <Breadcrumbs>
          <BreadcrumbLink onClick={handleNavigateToMyAccount}>My Account</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToMyBusinesses}>Businesses</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <BreadcrumbLink onClick={handleNavigateToSettings}>
            <BreadcrumbText>{business?.name}</BreadcrumbText>
          </BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>Team</span>
        </Breadcrumbs>
        
        {staffMembers.length > 0 ? (
          <StaffList>
            {staffMembers.map(staff => (
              <StaffCard key={staff.id} onClick={() => handleEditStaff(staff.id)}>
                <StaffInfo>
                  <ProfileImage>
                    <img 
                      src={staff.photoUrl || defaultProfilePic} 
                      alt={`${staff.firstName} ${staff.lastName}`}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = defaultProfilePic;
                      }}
                    />
                  </ProfileImage>
                  <StaffDetails>
                    <StaffName>{staff.firstName} {staff.lastName}</StaffName>
                    <StaffRoleAndEmail>
                      <StaffRole>{staff.role}</StaffRole>
                      <StaffEmail>{staff.email}</StaffEmail>
                    </StaffRoleAndEmail>
                  </StaffDetails>
                </StaffInfo>
                <ActionButton>
                  <OutlineButtonWrapper>
                    {/* <Button
                      text="Edit"
                      onClick={() => handleEditStaff(staff.id)}
                      bgColor="#000"
                      textColor="#000"
                      outline={true}
                      width="auto"
                      minWidth="180px"
                    /> */}
                  </OutlineButtonWrapper>
                </ActionButton>
              </StaffCard>
            ))}
          </StaffList>
        ) : (
          <EmptyState>
            <EmptyStateText>No Team members found. Add your first team member to get started.</EmptyStateText>
            <Button 
              text="Add"
              onClick={handleAddStaff}
              bgColor="#000"
              textColor="#fff"
              width="auto"
              minWidth="150px"
            />
          </EmptyState>        )}
      </ContentWrapper>
    </Container>
    </>
  );
};

export default BusinessStaffPage;